import { Link } from "react-router-dom";
import { Panel, EmptyState, money, count as fmt, pct1 } from "../lux";
import { humanMinutes } from "../PeriodPicker";
import { Users, Car } from "@phosphor-icons/react";

const Head = ({ children, right }) => (
  <div className={`label-mono ${right ? "text-right" : ""}`}>{children}</div>
);

// Top 5 by revenue for the selected period. Same numbers as /sales-performance.
export function TeamPerformancePanel({ rows, moneyOk, scope, me, label }) {
  const mine = scope === "self";
  return (
    <Panel
      label={`Team performance · ${label}`}
      title={mine ? "Your performance" : "Top salespeople"}
      icon={Users}
      testid="team-performance-panel"
      flush
      actions={<Link to="/sales-performance" className="text-xs text-primary hover:underline underline-offset-4" data-testid="team-performance-viewall">View all</Link>}
    >
      {(rows || []).length === 0 ? (
        <EmptyState icon={Users} title="No cars out in this period" hint="Figures cover rentals whose start date falls in this period and which actually went out, whenever the deal was sold." />
      ) : (
        <div className="divide-y divide-border/60">
          <div className={`px-5 py-2.5 grid ${moneyOk ? "grid-cols-12" : "grid-cols-9"} gap-3 items-center`}>
            <div className="col-span-4"><Head>Salesperson</Head></div>
            <div className="col-span-2"><Head right>Cars out</Head></div>
            {moneyOk && <div className="col-span-3"><Head right>Revenue (cars out)</Head></div>}
            <div className="col-span-2"><Head right>Replied</Head></div>
            <div className="col-span-1"><Head right>Close</Head></div>
          </div>
          {rows.map((r, i) => (
            <div key={r.salesperson} className={`px-5 py-3 grid ${moneyOk ? "grid-cols-12" : "grid-cols-9"} gap-3 items-center`} data-testid={`team-row-${r.salesperson}`}>
              <div className="col-span-4 min-w-0 flex items-center gap-2">
                <span className="label-mono w-4 shrink-0">{mine && me?.rank ? me.rank : i + 1}</span>
                <span className="font-heading text-base truncate">{r.salesperson}</span>
                {mine && <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-primary/15 text-primary">You</span>}
              </div>
              <div className="col-span-2 text-right tnum text-sm">{fmt(r.bookings)}</div>
              {moneyOk && <div className="col-span-3 text-right tnum text-sm text-primary">{money(r.revenue)}</div>}
              <div className="col-span-2 text-right tnum text-sm">
                {fmt(r.leads_replied)}
                <span className="text-[10px] text-muted-foreground ml-1">{humanMinutes(r.median_response_minutes)}</span>
              </div>
              <div className="col-span-1 text-right tnum text-sm">{r.lead_close && r.lead_close.of ? pct1(r.lead_close.rate) : "—"}</div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

export function TopVehiclesPanel({ rows, moneyOk, label }) {
  return (
    <Panel
      label={`Fleet earnings · ${label}`}
      title="Top earning vehicles"
      icon={Car}
      testid="top-vehicles-panel"
      flush
      actions={<Link to="/financials" className="text-xs text-primary hover:underline underline-offset-4" data-testid="top-vehicles-viewall">By vehicle</Link>}
    >
      {(rows || []).length === 0 ? (
        <EmptyState icon={Car} title="No earnings in this period" />
      ) : (
        <div className="divide-y divide-border/60">
          {rows.map((v) => (
            <div key={v.vehicle_id || v.vehicle} className="px-5 py-3 flex items-center gap-3" data-testid={`top-vehicle-${v.vehicle_id || v.vehicle}`}>
              <div className="flex-1 min-w-0">
                <div className="font-heading text-base truncate">{v.vehicle}</div>
                <div className="text-xs text-muted-foreground">{fmt(v.trips)} trip{v.trips === 1 ? "" : "s"}</div>
              </div>
              {moneyOk && (
                <div className="text-right shrink-0">
                  <div className="tnum text-sm text-primary">{money(v.revenue)}</div>
                  <div className="tnum text-xs text-emerald-400">{money(v.net_profit)} net</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
